import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import SimpleCard from 'views/Widgets/Cards/SimpleCard';
import Loading from 'views/Widgets/Loading';
import { useSummaryStatus } from './hooks';
import { SummaryGenerationStatus, SummaryGenerationStatusValue } from './types';

interface UnifiedSummaryGenerationCardProps {
  campaignId: number;
  reportItemId?: number;
  showEnpsSummary: boolean;
  showEngagementSurveySummary: boolean;
}

interface EnpsSummaryResponse {
  summary?: Record<string, unknown>;
  background_task_key?: string;
}

interface EngagementSummaryResponse {
  question_summaries?: unknown[];
  background_task_key?: string;
}

const UnifiedSummaryGenerationCard: FC<UnifiedSummaryGenerationCardProps> = ({
  campaignId,
  reportItemId,
  showEnpsSummary,
  showEngagementSurveySummary,
}) => {
  const proxyParameters = useProxyParameters();

  // Counters to force useConfirmApi to refetch after background tasks complete
  const [enpsRefetchVersion, setEnpsRefetchVersion] = useState(0);
  const [engagementRefetchVersion, setEngagementRefetchVersion] = useState(0);

  // Get summary status from context
  const { summaryStatus, setSummaryStatus, setEnpsData, setEngagementData } =
    useSummaryStatus();

  // Helper to update a specific summary status using functional update to avoid stale closures
  const updateEnpsStatus = useCallback(
    (
      status: SummaryGenerationStatusValue,
      backgroundTaskKey?: string | null,
      errorMessage: string | null = null
    ) => {
      setSummaryStatus((prev: SummaryGenerationStatus) => ({
        ...prev,
        enps: {
          status,
          // Use undefined check (not ??) so null explicitly clears the key
          backgroundTaskKey:
            backgroundTaskKey === undefined
              ? prev.enps.backgroundTaskKey
              : backgroundTaskKey,
          errorMessage,
        },
      }));
    },
    [setSummaryStatus]
  );

  const updateEngagementStatus = useCallback(
    (
      status: SummaryGenerationStatusValue,
      backgroundTaskKey?: string | null,
      errorMessage: string | null = null
    ) => {
      setSummaryStatus((prev: SummaryGenerationStatus) => ({
        ...prev,
        engagement: {
          status,
          // Use undefined check (not ??) so null explicitly clears the key
          backgroundTaskKey:
            backgroundTaskKey === undefined
              ? prev.engagement.backgroundTaskKey
              : backgroundTaskKey,
          errorMessage,
        },
      }));
    },
    [setSummaryStatus]
  );

  // Check existing eNPS summary status
  const { status: enpsApiStatus, data: enpsData } =
    useConfirmApi<EnpsSummaryResponse>({
      url: `/campaigns/${campaignId}/enps-summary`,
      method: 'GET',
      params: {
        report_item_id: reportItemId,
        v: enpsRefetchVersion,
      },
      disabled: !showEnpsSummary,
    });

  // Check existing engagement summary status
  const { status: engagementApiStatus, data: engagementData } =
    useConfirmApi<EngagementSummaryResponse>({
      url: `/campaigns/${campaignId}/engagement-survey-summary`,
      method: 'GET',
      params: {
        report_item_id: reportItemId,
        v: engagementRefetchVersion,
      },
      disabled: !showEngagementSurveySummary,
    });

  // Computed states for each summary
  const hasEnpsSummary = useMemo(
    () => enpsData?.summary && Object.keys(enpsData.summary ?? {}).length > 0,
    [enpsData?.summary]
  );

  const hasEngagementSummary = useMemo(
    () => (engagementData?.question_summaries?.length ?? 0) > 0,
    [engagementData?.question_summaries]
  );

  // Share fetched data with context so child components don't need to refetch
  useEffect(() => {
    if (enpsApiStatus === 'SUCCESS' && enpsData) {
      setEnpsData(enpsData);
    }
  }, [enpsApiStatus, enpsData, setEnpsData]);

  useEffect(() => {
    if (engagementApiStatus === 'SUCCESS' && engagementData) {
      setEngagementData(engagementData);
    }
  }, [engagementApiStatus, engagementData, setEngagementData]);

  // Update status when API returns data showing summary already exists
  useEffect(() => {
    if (
      enpsApiStatus === 'SUCCESS' &&
      hasEnpsSummary &&
      summaryStatus.enps.status === 'PENDING'
    ) {
      updateEnpsStatus('COMPLETED', null);
    }
  }, [
    enpsApiStatus,
    hasEnpsSummary,
    summaryStatus.enps.status,
    updateEnpsStatus,
  ]);

  useEffect(() => {
    if (
      engagementApiStatus === 'SUCCESS' &&
      hasEngagementSummary &&
      summaryStatus.engagement.status === 'PENDING'
    ) {
      updateEngagementStatus('COMPLETED', null);
    }
  }, [
    engagementApiStatus,
    hasEngagementSummary,
    summaryStatus.engagement.status,
    updateEngagementStatus,
  ]);

  // Check if API returned a background task key (generation already in progress)
  useEffect(() => {
    if (
      enpsData?.background_task_key &&
      summaryStatus.enps.status === 'PENDING'
    ) {
      updateEnpsStatus('IN_PROGRESS', enpsData.background_task_key);
    }
  }, [
    enpsData?.background_task_key,
    summaryStatus.enps.status,
    updateEnpsStatus,
  ]);

  useEffect(() => {
    if (
      engagementData?.background_task_key &&
      summaryStatus.engagement.status === 'PENDING'
    ) {
      updateEngagementStatus('IN_PROGRESS', engagementData.background_task_key);
    }
  }, [
    engagementData?.background_task_key,
    summaryStatus.engagement.status,
    updateEngagementStatus,
  ]);

  // Background task polling for eNPS
  const handleEnpsTaskSuccess = useCallback(() => {
    updateEnpsStatus('COMPLETED', null);
    setEnpsRefetchVersion((v) => v + 1);
  }, [updateEnpsStatus]);

  const handleEnpsTaskError = useCallback(
    (error: unknown) => {
      // useBackgroundTask passes the task's backend error message as a string
      updateEnpsStatus(
        'FAILED',
        null,
        typeof error === 'string' ? error : null
      );
    },
    [updateEnpsStatus]
  );

  useBackgroundTask({
    disabled: !summaryStatus.enps.backgroundTaskKey,
    taskDescriptor: {
      task_key: summaryStatus.enps.backgroundTaskKey ?? '',
      status: 'submitted',
    },
    onTaskCompleted: handleEnpsTaskSuccess,
    onError: handleEnpsTaskError,
  });

  // Background task polling for engagement
  const handleEngagementTaskSuccess = useCallback(() => {
    updateEngagementStatus('COMPLETED', null);
    setEngagementRefetchVersion((v) => v + 1);
  }, [updateEngagementStatus]);

  const handleEngagementTaskError = useCallback(
    (error: unknown) => {
      // useBackgroundTask passes the task's backend error message as a string
      updateEngagementStatus(
        'FAILED',
        null,
        typeof error === 'string' ? error : null
      );
    },
    [updateEngagementStatus]
  );

  useBackgroundTask({
    disabled: !summaryStatus.engagement.backgroundTaskKey,
    taskDescriptor: {
      task_key: summaryStatus.engagement.backgroundTaskKey ?? '',
      status: 'submitted',
    },
    onTaskCompleted: handleEngagementTaskSuccess,
    onError: handleEngagementTaskError,
  });

  // Check if we need to show this card at all
  const enpsNeedsGeneration =
    showEnpsSummary && summaryStatus.enps.status !== 'COMPLETED';
  const engagementNeedsGeneration =
    showEngagementSurveySummary &&
    summaryStatus.engagement.status !== 'COMPLETED';

  // Determine if any generation is in progress
  const isGenerating =
    summaryStatus.enps.status === 'IN_PROGRESS' ||
    summaryStatus.engagement.status === 'IN_PROGRESS';

  // Check if any summary has failed (for retry functionality)
  const hasFailedSummary =
    (showEnpsSummary && summaryStatus.enps.status === 'FAILED') ||
    (showEngagementSurveySummary &&
      summaryStatus.engagement.status === 'FAILED');

  // Determine if we're still loading initial status
  const isLoading =
    (showEnpsSummary && enpsApiStatus === 'LOADING') ||
    (showEngagementSurveySummary && engagementApiStatus === 'LOADING');

  // Handler to generate all summaries (also handles retry for failed)
  const generateAllHandler = useCallback(() => {
    // Generate eNPS if pending or failed
    const enpsStatus = summaryStatus.enps.status;
    if (
      showEnpsSummary &&
      (enpsStatus === 'PENDING' || enpsStatus === 'FAILED')
    ) {
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/campaigns/${campaignId}/generate-enps-summary`,
        {
          ...proxyParameters,
          report_item_id: reportItemId,
        },
        (response: { background_task_key?: string } | null, error: unknown) => {
          if (error) {
            updateEnpsStatus('FAILED', null);
            return;
          }
          if (response?.background_task_key) {
            updateEnpsStatus('IN_PROGRESS', response.background_task_key);
          }
        }
      );
    }

    // Generate engagement if pending or failed
    const engagementStatus = summaryStatus.engagement.status;
    if (
      showEngagementSurveySummary &&
      (engagementStatus === 'PENDING' || engagementStatus === 'FAILED')
    ) {
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/campaigns/${campaignId}/generate-engagement-survey-summary`,
        {
          ...proxyParameters,
          report_item_id: reportItemId,
        },
        (response: { background_task_key?: string } | null, error: unknown) => {
          if (error) {
            updateEngagementStatus('FAILED', null);
            return;
          }
          if (response?.background_task_key) {
            updateEngagementStatus('IN_PROGRESS', response.background_task_key);
          }
        }
      );
    }
  }, [
    campaignId,
    reportItemId,
    proxyParameters,
    showEnpsSummary,
    showEngagementSurveySummary,
    summaryStatus.enps.status,
    summaryStatus.engagement.status,
    updateEnpsStatus,
    updateEngagementStatus,
  ]);

  // Don't show if all relevant summaries already exist
  if (!enpsNeedsGeneration && !engagementNeedsGeneration) {
    return null;
  }

  // Render status indicator for a summary type
  const renderStatusIndicator = (
    label: React.ReactNode,
    status: SummaryGenerationStatusValue,
    errorMessage: string | null,
    isEnabled: boolean
  ) => {
    if (!isEnabled) return null;

    return (
      <div className="d-flex align-items-center mb-2">
        <span className="me-2">
          {status === 'COMPLETED' ? (
            <i className="fe fe-check-circle text-success" />
          ) : status === 'IN_PROGRESS' ? (
            <i className="fe fe-loader text-primary spin" />
          ) : status === 'FAILED' ? (
            <i className="fe fe-alert-circle text-danger" />
          ) : (
            <i className="fe fe-minus-circle text-muted" />
          )}
        </span>
        <span className="me-2">{label}</span>
        <span className="text-muted small">
          {status === 'PENDING' && (
            <FormattedMessage
              id="app.views.engagement.unified_summary_generation_card.status.ready"
              defaultMessage="Ready to generate"
            />
          )}
          {status === 'IN_PROGRESS' && (
            <FormattedMessage
              id="app.views.engagement.unified_summary_generation_card.status.generating"
              defaultMessage="Generating..."
            />
          )}
          {status === 'COMPLETED' && (
            <FormattedMessage
              id="app.views.engagement.unified_summary_generation_card.status.complete"
              defaultMessage="Complete"
            />
          )}
          {status === 'FAILED' &&
            (errorMessage ?? (
              <FormattedMessage
                id="app.views.engagement.unified_summary_generation_card.status.error"
                defaultMessage="Error - please try again"
              />
            ))}
        </span>
      </div>
    );
  };

  // Show loading state while checking existing summaries
  if (isLoading) {
    return (
      <SimpleCard
        title={
          <FormattedMessage
            id="app.views.engagement.unified_summary_generation_card.title"
            defaultMessage="AI Summaries"
          />
        }
      >
        <Loading
          message={
            <FormattedMessage
              id="app.views.engagement.unified_summary_generation_card.loading"
              defaultMessage="Checking summary status..."
            />
          }
        />
      </SimpleCard>
    );
  }

  return (
    <SimpleCard
      title={
        <FormattedMessage
          id="app.views.engagement.unified_summary_generation_card.title"
          defaultMessage="AI Summaries"
        />
      }
    >
      <p className="mb-3">
        {isGenerating ? (
          <FormattedMessage
            id="app.views.engagement.unified_summary_generation_card.generating_message"
            defaultMessage="AI is generating the summaries. This may take several minutes. The summaries will appear automatically when ready."
          />
        ) : (
          <FormattedMessage
            id="app.views.engagement.unified_summary_generation_card.description"
            defaultMessage="Generate AI summaries for this report."
          />
        )}
      </p>

      {renderStatusIndicator(
        <FormattedMessage
          id="app.views.engagement.unified_summary_generation_card.enps_label"
          defaultMessage="eNPS Summary"
        />,
        summaryStatus.enps.status,
        summaryStatus.enps.errorMessage,
        enpsNeedsGeneration
      )}

      {renderStatusIndicator(
        <FormattedMessage
          id="app.views.engagement.unified_summary_generation_card.engagement_label"
          defaultMessage="Engagement Survey Summary"
        />,
        summaryStatus.engagement.status,
        summaryStatus.engagement.errorMessage,
        engagementNeedsGeneration
      )}

      <Button
        color="primary"
        className="fs-4 mt-3"
        onClick={generateAllHandler}
        disabled={isGenerating}
      >
        {hasFailedSummary ? (
          <FormattedMessage
            id="app.views.engagement.unified_summary_generation_card.retry_button"
            defaultMessage="Retry Failed AI Summaries"
          />
        ) : (
          <FormattedMessage
            id="app.views.engagement.unified_summary_generation_card.generate_button"
            defaultMessage="Generate AI Summaries"
          />
        )}
      </Button>
    </SimpleCard>
  );
};

export default UnifiedSummaryGenerationCard;
